<template>
  <div class="uk-container full-page label-reader">
    <UserHeader />
    <div class="uk-flex uk-flex-between uk-flex-middle">
      <h1>Ingredient Checker</h1>
      <div class="uk-flex uk-flex-middle" style="gap: 8px;">
        <button v-if="result" class="uk-button uk-button-default uk-button-small" @click="startOver">
          <span uk-icon="icon: refresh; ratio: 0.8" class="uk-margin-small-right"></span>Scan Another Product
        </button>
        <router-link to="/ingredient-checker-history" class="uk-button uk-button-default uk-button-small">
          <span uk-icon="icon: history" class="uk-margin-small-right"></span>History
        </router-link>
      </div>
    </div>
    <p>
      Take a picture of any ingredient label and compare it directly to your UCARI results. If ingredients from your Red or Yellow categories are found, we'll highlight them. If none are found, we'll simply let you know the product appears compatible with your results.
    </p>
    <div
      v-if="ingredientCheckerSubscriptionEnabled"
      class="uk-margin-small-top subscription-note"
      :class="{
        'subscription-note--active': entitlementLoaded && entitlement.hasSubscription,
        'subscription-note--warning': !!entitlementError || (entitlementLoaded && !entitlement.hasSubscription && entitlement.freeChecksRemaining === 0),
        'subscription-note--free': !entitlementLoaded || (!entitlement.hasSubscription && entitlement.freeChecksRemaining > 0),
      }"
    >
      <div class="subscription-note__status">
        <span class="subscription-note__dot" aria-hidden="true"></span>
        <span class="subscription-note__text">{{ subscriptionNoticeText }}</span>
      </div>
      <button v-if="entitlementError" class="subscription-note__action" @click="fetchEntitlement()">Retry access check</button>
      <router-link v-else-if="entitlementLoaded" to="/ingredient-checker-subscription" class="subscription-note__action">
        {{ subscriptionActionText }}
      </router-link>
    </div>

    <!-- Kit selector -->
    <div v-if="loadingKits" class="uk-margin">
      <span uk-spinner="ratio: 0.6"></span> Loading your kits…
    </div>
    <div v-else-if="kitsError" class="uk-alert-warning" role="alert">
      <p>{{ kitsError }}</p>
      <button class="uk-button uk-button-default" @click="fetchKits">Retry loading kits</button>
    </div>
    <div v-else-if="!hasIdFromUrl && kits.length > 1" class="uk-margin">
      <label for="ingredient-checker-kit" class="uk-form-label uk-margin-right">Select a kit:</label>
      <select id="ingredient-checker-kit" ref="kitSelector" class="uk-select uk-width-1-3@s" v-model="kitId">
        <option value="" disabled>— Choose a kit —</option>
        <option v-for="kit in kits" :key="kit.KitId" :value="kit.KitId">
          {{ kit.FirstName }}<span v-if="kit.PetName"> ({{ kit.PetName }})</span>{{ kit.LastName ? ` ${kit.LastName}` : '' }} — {{ kit.SerialNumber }}
        </option>
      </select>
    </div>
    <div v-else-if="!hasIdFromUrl && kits.length === 0 && !loadingKits" class="uk-alert-warning" uk-alert>
      <p>No completed test kits found. <router-link to="/">Go to My Test Kits</router-link> to register a kit.</p>
    </div>

    <div class="uk-width-1-3">
      <input
        hidden
        id="kitId"
        class="uk-input"
        type="text"
        v-model="kitId"
        placeholder="XXXXXX-XXXXXX"
        @input="formatSerialNumber"
        :class="{ 'error-border': !kitId && submitted }"
      />
      <div class="uk-text-danger uk-text-small" v-if="kitIdError">
        {{ kitIdError }}
      </div>
    </div>

    <!-- Step indicator -->
    <div class="step-indicator uk-margin">
      <div class="step" :class="{ active: step === 1, done: step > 1 }">
        <span class="step-number">{{ step > 1 ? '✓' : '1' }}</span>
        <span class="step-label">Product Photo</span>
      </div>
      <div class="step-line" :class="{ filled: step > 1 }"></div>
      <div class="step" :class="{ active: step === 2, done: step > 2 }">
        <span class="step-number">{{ step > 2 ? '✓' : '2' }}</span>
        <span class="step-label">Ingredients Label</span>
      </div>
      <div class="step-line" :class="{ filled: !!result }"></div>
      <div class="step" :class="{ active: false, done: !!result }">
        <span class="step-number">{{ result ? '✓' : '3' }}</span>
        <span class="step-label">Results</span>
      </div>
    </div>

    <!-- STEP 1: Brand / Product image -->
    <div v-if="step === 1 && !result">
      <h3>Step 1: Capture the Brand &amp; Product Name</h3>
      <p class="upload-helper-text">
        Take a clear photo of the front of the package, choose an existing image from your device,
        or simply drag and drop an image into the box below. Make sure the brand and product name
        are easy to read.
      </p>

      <div
        class="drop-zone"
        :class="{ 'drop-zone--active': brandDragActive, 'drop-zone--has-file': !!brandFile }"
        @dragenter.prevent="onDragEnter('brand', $event)"
        @dragover.prevent="onDragOver('brand', $event)"
        @dragleave.prevent="onDragLeave('brand', $event)"
        @drop.prevent="onDrop('brand', $event)"
      >
        <div v-if="!brandPreviewUrl" class="drop-zone__placeholder">
          <span uk-icon="icon: cloud-upload; ratio: 2"></span>
          <p class="drop-zone__hint">
            <strong>Drag &amp; drop</strong> a product photo here, or use the option{{ showCameraCapture ? 's' : '' }} below.
          </p>
        </div>
        <div v-else class="drop-zone__preview">
          <img :src="brandPreviewUrl" alt="brand preview" class="upload-preview" />
        </div>

        <div class="upload-buttons">
          <label v-if="showCameraCapture" class="uk-button uk-button-primary uk-flex uk-flex-middle">
            <span uk-icon="icon: camera" class="uk-margin-small-right"></span>
            {{ brandFile ? 'Retake Photo' : 'Take Photo' }}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              @change="onBrandFileChange"
              hidden
            />
          </label>
          <label class="uk-button uk-button-default uk-flex uk-flex-middle">
            <span uk-icon="icon: image" class="uk-margin-small-right"></span>
            {{ brandFile ? 'Choose Different Photo' : 'Choose From Library' }}
            <input
              type="file"
              accept="image/*"
              @change="onBrandFileChange"
              hidden
            />
          </label>
        </div>
      </div>

      <button
        class="uk-button uk-button-primary uk-margin-small-top"
        :disabled="!brandFile"
        @click="step = 2"
      >
        Next: Ingredients Label →
      </button>
    </div>

    <!-- STEP 2: Ingredients label image -->
    <div v-if="step === 2 && !result">
      <h3>Step 2: Capture the Ingredients Label</h3>
      <p class="upload-helper-text">
        Take a clear photo of the ingredient list on the back of the package, choose an existing
        image from your device, or drag and drop an image into the box below. Make sure the
        ingredient text is in focus.
      </p>

      <div
        class="drop-zone"
        :class="{ 'drop-zone--active': ingredientDragActive, 'drop-zone--has-file': !!ingredientFile }"
        @dragenter.prevent="onDragEnter('ingredient', $event)"
        @dragover.prevent="onDragOver('ingredient', $event)"
        @dragleave.prevent="onDragLeave('ingredient', $event)"
        @drop.prevent="onDrop('ingredient', $event)"
      >
        <div v-if="!ingredientPreviewUrl" class="drop-zone__placeholder">
          <span uk-icon="icon: cloud-upload; ratio: 2"></span>
          <p class="drop-zone__hint">
            <strong>Drag &amp; drop</strong> an ingredients photo here, or use the option{{ showCameraCapture ? 's' : '' }} below.
          </p>
        </div>
        <div v-else class="drop-zone__preview">
          <img :src="ingredientPreviewUrl" alt="ingredients preview" class="upload-preview" />
        </div>

        <div class="upload-buttons">
          <label v-if="showCameraCapture" class="uk-button uk-button-primary uk-flex uk-flex-middle">
            <span uk-icon="icon: camera" class="uk-margin-small-right"></span>
            {{ ingredientFile ? 'Retake Photo' : 'Take Photo' }}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              @change="onIngredientFileChange"
              hidden
            />
          </label>
          <label class="uk-button uk-button-default uk-flex uk-flex-middle">
            <span uk-icon="icon: image" class="uk-margin-small-right"></span>
            {{ ingredientFile ? 'Choose Different Photo' : 'Choose From Library' }}
            <input
              type="file"
              accept="image/*"
              @change="onIngredientFileChange"
              hidden
            />
          </label>
        </div>
      </div>

      <!-- Both-image thumbnails -->
      <div v-if="brandPreviewUrl" class="uk-margin-top dual-preview">
        <div class="preview-thumb">
          <div class="uk-text-small uk-text-bold">Product</div>
          <img :src="brandPreviewUrl" alt="brand" class="label-preview-sm" />
        </div>
        <div v-if="ingredientPreviewUrl" class="preview-thumb">
          <div class="uk-text-small uk-text-bold">Ingredients</div>
          <img :src="ingredientPreviewUrl" alt="ingredients" class="label-preview-sm" />
        </div>
      </div>

      <div class="uk-margin-small-top" style="display:flex; gap:8px;">
        <button class="uk-button uk-button-secondary" @click="step = 1">
          ← Back
        </button>
        <button
          class="uk-button uk-button-primary"
          @click="submit"
          :disabled="!brandFile || !ingredientFile || loading || !kitId || !!kitIdError"
          :aria-describedby="!kitId ? 'ingredient-checker-submit-help' : undefined"
        >
          {{ loading ? "Analyzing…" : "Analyze Ingredients" }}
        </button>
      </div>
      <p v-if="!kitId" id="ingredient-checker-submit-help" class="uk-margin-small-top" role="status">
        Select a test kit before analyzing these photos.
        <button v-if="kits.length > 1" class="uk-button uk-button-text" @click="focusKitSelector">Choose a kit</button>
      </p>
      <p v-if="entitlementError" class="uk-margin-small-top" role="status">{{ entitlementError }}</p>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="loading-panel uk-margin-top">
      <div class="loading-spinner-wrap">
        <span uk-spinner="ratio: 1.6"></span>
      </div>
      <p class="loading-title">Analyzing your product label…</p>
      <div class="loading-progress" role="progressbar" aria-label="Analyzing ingredients">
        <div class="loading-progress__bar"></div>
      </div>
      <p class="loading-tip">{{ loadingTips[loadingTipIndex] }}</p>
      <p class="loading-note">This may take up to a minute — hang tight!</p>
    </div>

    <div v-if="error" class="error-msg">
      <div>{{ error }}</div>
      <router-link
        v-if="ingredientCheckerSubscriptionEnabled && subscriptionRequired"
        to="/ingredient-checker-subscription"
        class="uk-button uk-button-primary uk-button-small uk-margin-small-top"
      >
        Subscribe for Unlimited Checks
      </router-link>
    </div>

    <!-- ===== RESULTS ===== -->
    <div v-if="result" ref="resultsSection">

      <!-- Image previews + product name header -->
      <div class="result-header">
        <div class="result-images">
          <div class="result-img-wrap">
            <div class="img-label">Product</div>
            <img :src="brandPreviewUrl" alt="product" class="result-img" @click="openLightbox(brandPreviewUrl)" />
          </div>
          <div class="result-img-wrap">
            <div class="img-label">Ingredients</div>
            <img :src="ingredientPreviewUrl" alt="ingredients" class="result-img" @click="openLightbox(ingredientPreviewUrl)" />
          </div>
        </div>
        <div v-if="result.product_name_guess" class="product-name">
          {{ result.product_name_guess }}
        </div>
        <div class="result-actions" style="display:none;">
          <button class="uk-button uk-button-small uk-button-default" @click="startOver">
            <span uk-icon="icon: refresh; ratio: 0.8" class="uk-margin-small-right"></span>Scan Another Product
          </button>
        </div>
      </div>

      <IngredientCheckNotice :result="result" />

      <!-- Ingredients Found in This Product -->
      <div v-if="result.ingredients_raw" class="result-card">
        <div class="result-card__header">
          <span class="result-card__icon" uk-icon="icon: list; ratio: 1.1"></span>
          <h3 class="result-card__title">Text Read From Your Ingredient List</h3>
        </div>
        <pre class="ingredients-pre">{{ result.ingredients_raw }}</pre>
      </div>

      <!-- Intolerance matches -->
      <div v-if="sortedMatches.length" class="result-card">
        <div class="result-card__header">
          <span class="result-card__icon" uk-icon="icon: warning; ratio: 1.1"></span>
          <h3 class="result-card__title">Matches Your Intolerances</h3>
        </div>
        <div class="match-list uk-margin-small-top">
          <div v-for="(m, idx) in sortedMatches" :key="'m' + idx" class="match-item">
            <span class="match-item__intolerance">{{ m.test_item_name }}</span>
            <span class="match-item__arrow">→</span>
            <span class="match-item__ingredient">{{ m.matched_ingredient }}</span>
            <span class="match-item__severity" :class="'severity--' + normalizedSeverity(m)">
              {{ severityLabel(m) }}
            </span>
          </div>
        </div>
      </div>
      <div v-else-if="canShowNoMatches" class="result-card result-card--success">
        <div class="result-card__header">
          <span class="result-card__icon" uk-icon="icon: check; ratio: 1.1"></span>
          <h3 class="result-card__title">No Intolerance Matches Found</h3>
        </div>
        <p class="result-card__subtitle">
          We didn't find any ingredients in this product that clearly match your intolerance report.
        </p>
      </div>

      <!-- Ingredients not on intolerance list -->
      <div v-if="result.comparison?.analysis_status === 'complete' && result.comparison?.not_matched?.length" class="result-card result-card--info">
        <div class="result-card__header">
          <span class="result-card__icon" uk-icon="icon: info; ratio: 1.1"></span>
          <h3 class="result-card__title">Intolerance Items Not Found in This Product</h3>
        </div>
        <p class="result-card__subtitle">Your results flagged these items, but they were not detected in this product.</p>
        <div class="not-matched-chips">
          <span v-for="(nm, idx) in result.comparison.not_matched" :key="'nm' + idx" class="chip chip--safe">
            {{ nm.test_item_name }}
          </span>
        </div>
      </div>

      <!-- What This Means -->
      <div v-if="result.comparison?.notes" class="result-card">
        <div class="result-card__header">
          <span class="result-card__icon" uk-icon="icon: comment; ratio: 1.1"></span>
          <h3 class="result-card__title">What This Means</h3>
        </div>
        <p class="result-card__notes">{{ result.comparison.notes }}</p>
      </div>

      <!-- Disclaimer -->
      <div class="disclaimer-box">
        <p class="disclaimer-text">
          <strong>Disclaimer:</strong> This tool automatically reads product labels and compares ingredients against your intolerance test results. Results are for informational purposes only and should not be considered veterinary or medical advice. Ingredient detection may not be 100% accurate due to image quality, label formatting, or automated interpretation. The results reflect potential intolerances identified through testing and do not constitute an evaluation or endorsement of any product or brand. Consult your healthcare provider or veterinarian as appropriate before making dietary changes.
        </p>
      </div>

    </div>

    <!-- Image lightbox -->
    <div v-if="lightboxUrl" class="lightbox-overlay" @click="lightboxUrl = ''">
      <img :src="lightboxUrl" alt="full size" class="lightbox-img" />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import UserHeader from "./UserHeader.vue";
import IngredientCheckNotice from './IngredientCheckNotice.vue';
import { canShowNoMatches } from '../utils/ingredientCheckResult';
import { ingredientCheckerAccessNotice, loadIngredientCheckerAccess } from '../utils/ingredientCheckerAccess';
import { isIngredientCheckerEnabledForCid } from "../utils/common";
import { logActivity } from "@/utils/activity";

export default {
  name: 'LabelReader',
  created() {
    document.title = 'Ingredient Checker | UCARI';
    this.loadCredentials();
    const id = this.$route.query.id;
    if (id) {
      this.kitId = id;
      this.hasIdFromUrl = true;
    } else {
      this.fetchKits();
    }
  },
  mounted() {
    this.logActivity(50);
    this.showCameraCapture = this.detectCameraCaptureAvailability();
  },
  components: { UserHeader, IngredientCheckNotice },

  data() {
    return {
      // kit id
      kitId: "",
      kitIdError: "",
      submitted: false,
      hasIdFromUrl: false,
      kits: [],
      loadingKits: false,
      kitsError: '',

      // step tracking (1 = brand image, 2 = ingredient image + submit)
      step: 1,

      // Step 1: brand / product image
      brandFile: null,
      brandPreviewUrl: "",
      brandMeta: { originalBytes: 0, compressedBytes: 0, note: "" },

      // Step 2: ingredient label image
      ingredientFile: null,
      ingredientPreviewUrl: "",
      ingredientMeta: { originalBytes: 0, compressedBytes: 0, note: "" },

      showCameraCapture: false,

      // request state
      loading: false,
      error: "",
      subscriptionRequired: false,
      result: null,
      entitlementLoaded: false,
      entitlementLoading: false,
      entitlementError: '',
      entitlementRequestId: 0,
      entitlement: {
        completedChecks: 0,
        freeChecksIncluded: 1,
        freeChecksRemaining: 1,
        hasSubscription: false,
        needsBillingAttention: false,
        canScan: true,
        hasFreeIngredientChecker: false,
        activeKitEntitlement: null,
      },

      // UI toggles
      lightboxUrl: "",

      // Drag-and-drop visual state
      brandDragActive: false,
      ingredientDragActive: false,
      brandDragCounter: 0,
      ingredientDragCounter: 0,

      // Loading tips
      loadingTipIndex: 0,
      loadingTipTimer: null,
      loadingTips: [
        "Reading your product label…",
        "Identifying ingredients from the photo…",
        "Cross-referencing with your intolerance results…",
        "Checking for common ingredient synonyms and variants…",
        "Almost there — compiling your personalized report…",
      ],

      // Auth headers
      cid: "",
      accessToken: "",
    };
  },

  computed: {
    canShowNoMatches() { return canShowNoMatches(this.result); },
    sortedMatches() {
      const matches = this.result?.comparison?.matched || [];
      const severityOrder = { severe: 0, moderate: 1 };
      const confidenceOrder = { high: 0, medium: 1, low: 2 };
      return [...matches].sort((a, b) => {
        const severityDiff = (severityOrder[this.normalizedSeverity(a)] ?? 3) - (severityOrder[this.normalizedSeverity(b)] ?? 3);
        if (severityDiff !== 0) return severityDiff;
        return (confidenceOrder[a.confidence] ?? 3) - (confidenceOrder[b.confidence] ?? 3);
      });
    },
    subscriptionNoticeText() {
      return ingredientCheckerAccessNotice(this);
    },
    subscriptionActionText() {
      if (!this.entitlementLoaded) return 'View plans';
      return (this.entitlement.hasSubscription || this.entitlement.needsBillingAttention) ? 'Manage plan' : 'View plans';
    },
    ingredientCheckerSubscriptionEnabled() {
      return isIngredientCheckerEnabledForCid(this.cid);
    },
  },

  watch: {
    kitId(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.kitIdError = '';
      this.fetchEntitlement(newValue);
    },
  },

  methods: {
    logActivity,
    detectCameraCaptureAvailability() {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
      }

      const userAgent = navigator.userAgent || '';
      const mobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const iPadDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

      return mobileBrowser || iPadDesktopMode;
    },
    isIngredientCheckerEligibleKit(kit) {
      return !!kit?.ResultDateUtc;
    },
    loadCredentials() {
      this.accessToken = localStorage.getItem('accessToken');
      this.cid = localStorage.getItem('cid');
      const isProValue = localStorage.getItem('isPro');
      this.isPro = isProValue === '1' || isProValue === 'true';
    },
    async fetchEntitlement(kitId = this.kitId) {
      return loadIngredientCheckerAccess(this, kitId, id =>
        axios.get(`${process.env.VUE_APP_API_URL}/api/label-reader/access`, {
          timeout: 20000,
          params: {
            kitId: id,
          },
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid,
          }
        }));
    },
    async fetchKits() {
      this.loadingKits = true;
      this.kitsError = '';
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/test-kits`, {
          timeout: 20000,
          params: { page: 1, pageSize: 200 },
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid
          }
        });
        const items = (response.data.items || []).filter(k => this.isIngredientCheckerEligibleKit(k));
        this.kits = items;
        if (items.length === 1) {
          this.kitId = items[0].KitId;
        }
      } catch (err) {
        console.error('Failed to load kits:', err);
        this.kitsError = 'We could not load your test kits. Please retry. Your photos are still selected.';
      } finally {
        this.loadingKits = false;
      }
    },
    formatSerialNumber(event) {
      const input = event.target.value.replace(/[^0-9]/g, "").substring(0, 10);
      this.kitId = input;
      this.kitIdError = input ? "" : "Kit ID is required.";
    },

    focusKitSelector() {
      this.$refs.kitSelector?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.$refs.kitSelector?.focus({ preventScroll: true });
    },

    openLightbox(url) {
      if (url) this.lightboxUrl = url;
    },

    normalizedSeverity(match) {
      const value = String(match?.severity || match?.severity_label || "").toLowerCase();
      if (value === "severe" || value === "high") return "severe";
      if (value === "moderate" || value === "mild" || value === "medium") return "moderate";
      return "unknown";
    },

    severityLabel(match) {
      const severity = this.normalizedSeverity(match);
      if (severity === "severe") return "Severe";
      if (severity === "moderate") return "Moderate";
      return "Matched";
    },

    startOver() {
      this.result = null;
      this.brandFile = null;
      this.brandPreviewUrl = "";
      this.ingredientFile = null;
      this.ingredientPreviewUrl = "";
      this.step = 1;
      this.error = "";
      this.subscriptionRequired = false;
    },

    startLoadingTips() {
      this.loadingTipIndex = 0;
      this.loadingTipTimer = setInterval(() => {
        this.loadingTipIndex = (this.loadingTipIndex + 1) % this.loadingTips.length;
      }, 6000);
    },

    stopLoadingTips() {
      if (this.loadingTipTimer) {
        clearInterval(this.loadingTipTimer);
        this.loadingTipTimer = null;
      }
    },

    // --- Image handling + compression ---
    async onBrandFileChange(e) {
      const original = e.target.files?.[0] || null;
      await this.processBrandFile(original);
    },

    async onIngredientFileChange(e) {
      const original = e.target.files?.[0] || null;
      await this.processIngredientFile(original);
    },

    // --- Drag & drop handlers ---
    // Use a counter to ignore dragenter/dragleave bubbling from child elements,
    // so the highlight doesn't flicker as the user moves over inner content.
    onDragEnter(which, e) {
      if (!this.eventHasFiles(e)) return;
      if (which === "brand") {
        this.brandDragCounter++;
        this.brandDragActive = true;
      } else {
        this.ingredientDragCounter++;
        this.ingredientDragActive = true;
      }
    },
    onDragOver(which, e) {
      if (!this.eventHasFiles(e)) return;
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      if (which === "brand") this.brandDragActive = true;
      else this.ingredientDragActive = true;
    },
    onDragLeave(which) {
      if (which === "brand") {
        this.brandDragCounter = Math.max(0, this.brandDragCounter - 1);
        if (this.brandDragCounter === 0) this.brandDragActive = false;
      } else {
        this.ingredientDragCounter = Math.max(0, this.ingredientDragCounter - 1);
        if (this.ingredientDragCounter === 0) this.ingredientDragActive = false;
      }
    },
    async onDrop(which, e) {
      if (which === "brand") {
        this.brandDragActive = false;
        this.brandDragCounter = 0;
      } else {
        this.ingredientDragActive = false;
        this.ingredientDragCounter = 0;
      }
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      if (!file.type || !file.type.startsWith("image/")) {
        this.error = "Please drop an image file (JPG, PNG, WEBP, or HEIC).";
        return;
      }
      this.error = "";
      if (which === "brand") await this.processBrandFile(file);
      else await this.processIngredientFile(file);
    },

    eventHasFiles(e) {
      const types = e?.dataTransfer?.types;
      if (!types) return false;
      // DataTransferItemList exposes types as DOMStringList in some browsers
      for (let i = 0; i < types.length; i++) {
        if (types[i] === "Files") return true;
      }
      return false;
    },

    async processBrandFile(original) {
      if (this.brandPreviewUrl) URL.revokeObjectURL(this.brandPreviewUrl);
      this.brandFile = null;
      this.brandPreviewUrl = "";
      this.brandMeta = { originalBytes: original?.size || 0, compressedBytes: 0, note: "" };
      this.result = null;
      this.error = "";
      if (!original) return;

      try {
        const compressed = await this.compressImageFile(original, { maxDim: 1000, quality: 0.72, mimeType: "image/jpeg" });
        this.brandFile = compressed;
        this.brandMeta.compressedBytes = compressed.size;
        this.brandPreviewUrl = URL.createObjectURL(compressed);
        if (compressed.size >= original.size * 0.95) {
          this.brandMeta.note = "Already small / minimal compression";
        }
      } catch (err) {
        console.warn("Brand image compression failed; using original:", err);
        this.brandFile = original;
        this.brandPreviewUrl = URL.createObjectURL(original);
        this.brandMeta.note = "Compression failed; uploaded original";
      }
    },

    async processIngredientFile(original) {
      if (this.ingredientPreviewUrl) URL.revokeObjectURL(this.ingredientPreviewUrl);
      this.ingredientFile = null;
      this.ingredientPreviewUrl = "";
      this.ingredientMeta = { originalBytes: original?.size || 0, compressedBytes: 0, note: "" };
      this.result = null;
      this.error = "";
      if (!original) return;

      try {
        const compressed = await this.compressImageFile(original, { maxDim: 2000, quality: 0.9, mimeType: "image/jpeg" });
        this.ingredientFile = compressed;
        this.ingredientMeta.compressedBytes = compressed.size;
        this.ingredientPreviewUrl = URL.createObjectURL(compressed);
        if (compressed.size >= original.size * 0.95) {
          this.ingredientMeta.note = "Already small / minimal compression";
        }
      } catch (err) {
        console.warn("Ingredient image compression failed; using original:", err);
        this.ingredientFile = original;
        this.ingredientPreviewUrl = URL.createObjectURL(original);
        this.ingredientMeta.note = "Compression failed; uploaded original";
      }
    },

    async compressImageFile(file, { maxDim = 1200, quality = 0.78, mimeType = "image/jpeg" } = {}) {
      // If it's not an image, just return it
      if (!file.type?.startsWith("image/")) return file;

      // Some iPhones can produce HEIC which most browsers can't draw to canvas.
      // If browser can't decode it, createImageBitmap/img load will fail.
      const img = await this.loadImageFromFile(file);

      const { width: w, height: h } = img;
      const scale = Math.min(1, maxDim / Math.max(w, h));
      const targetW = Math.max(1, Math.round(w * scale));
      const targetH = Math.max(1, Math.round(h * scale));

      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;

      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, targetW, targetH);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
          mimeType,
          quality
        );
      });

      // Make a File so FormData sees a filename
      const outName = this.swapExtension(file.name || "label.jpg", mimeType === "image/png" ? "png" : "jpg");
      return new File([blob], outName, { type: mimeType });
    },

    loadImageFromFile(file) {
      // Prefer createImageBitmap when available (faster), fall back to <img>
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);

        const cleanup = () => {
          URL.revokeObjectURL(url);
        };

        if ("createImageBitmap" in window) {
          createImageBitmap(file)
            .then((bmp) => {
              cleanup();
              resolve(bmp);
            })
            .catch(() => {
              // fallback to <img>
              const img = new Image();
              img.onload = () => {
                cleanup();
                resolve(img);
              };
              img.onerror = (e) => {
                cleanup();
                reject(e);
              };
              img.src = url;
            });
        } else {
          const img = new Image();
          img.onload = () => {
            cleanup();
            resolve(img);
          };
          img.onerror = (e) => {
            cleanup();
            reject(e);
          };
          img.src = url;
        }
      });
    },

    swapExtension(name, ext) {
      const base = String(name).replace(/\.[^.]+$/, "");
      return `${base}.${ext}`;
    },

    formatBytes(bytes = 0) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let b = bytes;
      let i = 0;
      while (b >= 1024 && i < units.length - 1) {
        b /= 1024;
        i++;
      }
      return `${b.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    },

    async submit() {
      this.submitted = true;

      if (!this.kitId) {
        this.kitIdError = 'Select a test kit before analyzing these photos.';
        this.focusKitSelector();
        return;
      }
      if (this.kitIdError) return;
      if (!this.brandFile || !this.ingredientFile) return;

      this.loading = true;
      this.error = "";
      this.subscriptionRequired = false;
      this.result = null;
      this.startLoadingTips();

      this.$nextTick(() => {
        const el = document.querySelector('.loading-panel');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      try {
        const form = new FormData();
        form.append("brand_image", this.brandFile);
        form.append("ingredient_image", this.ingredientFile);
        form.append("kit_id", this.kitId);
        const resp = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/label-reader/ingredients`,
          form,
          {
            headers: {
              'Accept': 'application/json, text/plain, */*',
              'Authorization': `Bearer ${this.accessToken}`,
              'cid': this.cid,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.result = resp.data;
        this.$nextTick(() => {
          this.$refs.resultsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      } catch (err) {
        if (err.response?.status === 429) {
          this.error = err.response?.data?.message || "Ingredient checking quota exceeded. Try again later.";
        } else if (
          err.response?.status === 402
          || err.response?.data?.code === "ingredient_checker_subscription_required"
        ) {
          this.subscriptionRequired = true;
          this.entitlement = {
            ...this.entitlement,
            completedChecks: Math.max(this.entitlement.completedChecks, 1),
            freeChecksRemaining: 0,
            hasSubscription: false,
            canScan: false,
          };
          this.error =
            err.response?.data?.error ||
            "Your free ingredient check has already been used. Subscribe to continue.";
        } else {
          this.error =
            err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Request failed";
        }
      } finally {
        this.stopLoadingTips();
        this.loading = false;
        this.submitted = false;
      }
    },
  },

  beforeUnmount() {
    this.stopLoadingTips();
    if (this.brandPreviewUrl) URL.revokeObjectURL(this.brandPreviewUrl);
    if (this.ingredientPreviewUrl) URL.revokeObjectURL(this.ingredientPreviewUrl);
  },
};
</script>

<style scoped>
.subscription-note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: fit-content;
  max-width: 100%;
  min-height: 38px;
  padding: 6px 8px 6px 12px;
  border: 1px solid #d8dee6;
  border-radius: 6px;
  background: #fff;
  color: #364152;
  font-size: 13px;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.subscription-note--active {
  border-color: #b8dec7;
  background: #f6fbf7;
  color: #1f5132;
}

.subscription-note--free {
  border-color: #d7dce3;
  background: #f8fafc;
  color: #344054;
}

.subscription-note--warning {
  border-color: #f0c4bd;
  background: #fff7f5;
  color: #8a2f23;
}

.subscription-note__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.subscription-note__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #98a2b3;
  flex: 0 0 auto;
}

.subscription-note--active .subscription-note__dot {
  background: #16a34a;
}

.subscription-note--free .subscription-note__dot {
  background: #64748b;
}

.subscription-note--warning .subscription-note__dot {
  background: #d92d20;
}

.subscription-note__text {
  font-weight: 600;
  white-space: nowrap;
}

.subscription-note__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid currentColor;
  border-radius: 4px;
  color: inherit;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.72);
}

.subscription-note__action:hover,
.subscription-note__action:focus {
  color: inherit;
  text-decoration: none;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
}

.upload-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

@media (max-width: 640px) {
  .subscription-note {
    width: 100%;
  }

  .subscription-note__text {
    white-space: normal;
  }
}
</style>
